import useScrollReveal from '../useScrollReveal';
import useSplitText from '../useSplitText';
import { sectionStyles, getAnimation, resolveImage } from '../utils';
import SectionDivider from './SectionDivider';

export default function QuoteSection({ block = {}, primaryColor, secondaryColor, enableAnimations, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const splitRef = useSplitText({ type: 'words', animation: 'fade-up', stagger: 0.03, disabled: !enableAnimations });
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'editorial';

    const quote = content.quote || '';
    const author = content.author || '';
    const role = content.role || '';
    const imageUrl = resolveImage(content, 'image', mediaFolders) || content.image_url || '';

    if (!quote) return null;

    // ── Card variant ──
    if (variant === 'card') {
        return (
            <section className={`${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <div className="max-w-3xl mx-auto text-center rounded-3xl p-10 md:p-14"
                        style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.06)' : '#ffffff', boxShadow: s.dark ? 'none' : '0 20px 60px rgba(0,0,0,0.08)' }}>
                        {imageUrl && (
                            <div className="w-20 h-20 rounded-full overflow-hidden mx-auto mb-6 border-4 shadow-lg" style={{ borderColor: s.accent }}>
                                <img src={imageUrl} alt={author} className="w-full h-full object-cover" />
                            </div>
                        )}
                        <div className="text-5xl leading-none mb-4" style={{ color: s.accent }}>"</div>
                        <blockquote className="text-xl md:text-2xl font-medium leading-relaxed italic" style={{ color: s.text }}>
                            {quote}
                        </blockquote>
                        <div className="mt-8">
                            {author && <p className="text-lg font-bold" style={{ color: s.text }}>{author}</p>}
                            {role && <p className="text-sm mt-1" style={{ color: s.textSub }}>{role}</p>}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color="transparent" position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Editorial variant (default) ──
    return (
        <section className={`${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`} style={s.bgStyle}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
                    {imageUrl && (
                        <div className="md:col-span-4 lg:col-span-3">
                            <div className="w-48 h-48 md:w-full md:h-auto md:aspect-square rounded-2xl overflow-hidden mx-auto shadow-xl">
                                <img src={imageUrl} alt={author} className="w-full h-full object-cover" loading="lazy" />
                            </div>
                        </div>
                    )}
                    <div className={imageUrl ? 'md:col-span-8 lg:col-span-9' : 'md:col-span-12'}>
                        <div className="text-7xl md:text-8xl font-serif leading-none -mb-6 md:-mb-8 opacity-20" style={{ color: s.accent }}>"</div>
                        <blockquote ref={splitRef} className="text-2xl md:text-3xl lg:text-4xl font-medium leading-snug" style={{ color: s.text }}>
                            {quote}
                        </blockquote>
                        <div className="mt-8 flex items-center gap-4">
                            <div className="w-12 h-0.5 rounded-full" style={{ backgroundColor: s.accent }} />
                            <div>
                                {author && <p className="text-lg font-bold" style={{ color: s.text }}>{author}</p>}
                                {role && <p className="text-sm" style={{ color: s.textSub }}>{role}</p>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color="transparent" position="bottom"  config={config}/>
        </section>
    );
}
